import * as React from 'react';
import { usePriceStream, useRealtimeServerNow } from 'context/RealtimePricesContext';
import { marketSessionPhase, type MarketSessionPhase } from 'utils/shared/time/marketSession';
import { useMarketClockMs } from 'hooks/useMarketSessionClock';

/**
 * How old a streamed quote may be and still count as live, per session.
 *
 * The regular session prints constantly, so a tight window is right — a quote older than this means
 * the transport is behind, not that the market is quiet.
 *
 * Extended hours are a different instrument. The pre- and post-market tape is genuinely sparse:
 * SPY printed 56 one-minute bars across an entire 5.5-hour pre-market session, and thinner names go
 * many minutes between trades. A 15-second window applied there rejects almost every quote we
 * receive and silently falls back to a frozen number, which is exactly why the spot price has never
 * appeared to move outside the cash session. The gap between prints is a fact about the market, not
 * a fault in the feed, so the window is sized to the session.
 */
export const QUOTE_FRESH_MS: Record<MarketSessionPhase, number> = {
  regular: 30_000,
  pre: 10 * 60_000,
  after: 10 * 60_000,
  closed: 0,
};

/**
 * Is a quote of this age still live for this session?
 *
 * A negative age means the producer stamp is ahead of our clock; treat that as not-fresh rather
 * than trusting it, so a skewed clock can never light the dot indefinitely.
 */
export const isQuoteFresh = (phase: MarketSessionPhase, ageMs: number): boolean =>
  ageMs >= 0 && ageMs <= QUOTE_FRESH_MS[phase];

export interface LiveSpot {
  /** The streamed price when one is fresh for this session, else null. */
  price: number | null;
  /** Whether the dot should be lit. False whenever the market is closed. */
  isLive: boolean;
  /** The running session, for the dot's colour. `null` until the clock is read on the client. */
  phase: MarketSessionPhase | null;
}

/**
 * The live spot price for one ticker, plus the session that should colour its dot.
 *
 * One hook so every price in the product agrees — the header dot and the dot beside a number are
 * derived from the same session value and cannot contradict each other.
 *
 * Degrades quietly: with the realtime transport disabled the stream stays empty, `price` is null,
 * and callers keep whatever price they already had. The session phase still resolves, so the dot is
 * still correctly coloured and the panel is still correct — no environment flag gates the part of
 * this that answers "when does data update?".
 */
export const useLiveSpot = (ticker: string): LiveSpot => {
  const symbols = React.useMemo(() => (ticker ? [ticker] : []), [ticker]);
  const quotes = usePriceStream(symbols);
  const serverNow = useRealtimeServerNow();

  // The one app-wide session clock. `nowMs` advances on every shared tick, which is also what
  // re-evaluates freshness below — the extended tape is deliberately sparse, so a quote's `quote`
  // reference can sit unchanged for minutes, and without a ticking input a quote that was fresh on
  // arrival would stay lit long after it aged past its window.
  const nowMs = useMarketClockMs();

  const quote = ticker ? quotes[ticker.toUpperCase()] : undefined;

  return React.useMemo(() => {
    const phase = nowMs === null ? null : marketSessionPhase(nowMs);

    if (phase === null || phase === 'closed') {
      return { price: null, isLive: false, phase };
    }

    if (!quote || !Number.isFinite(quote.price)) {
      return { price: null, isLive: false, phase };
    }

    // Producer-stamped `ts` against a skew-corrected clock, so a drifting client clock does not
    // silently age out good quotes. Recomputed on each `nowMs` tick, so a quote ages out of its
    // session window even while no new print has arrived to change `quote`.
    const fresh = isQuoteFresh(phase, serverNow() - quote.ts);

    return {
      price: fresh ? quote.price : null,
      isLive: fresh,
      phase,
    };
  }, [nowMs, quote, serverNow]);
};

export default useLiveSpot;
